import { Buyer } from '~/models/marketplace/buyer'
import {
  CartSellerInfo,
  CartSellerItemProduct,
  CartShippingTypeCostOfShipping
} from '~/models/marketplace/cart'
import { Pagination } from '~/models/search/types'
import { SchemaField } from '~/models/shared/types'
import { ShippingAddress } from '~/models/marketplace/shipping-address'
import { MarketplaceReview } from '~/models/marketplace/reviews'

export interface OrderCheckoutSession {
  paymentId: string
  session: string
  url: string
}

export enum OrderStatus {
  PENDING_ACCEPTANCE = 'pending_acceptance',
  ACCEPTED = 'accepted',
  PENDING_COLLECTION_OF_ITEMS = 'pending_collection_of_items',
  PENDING_SHIPMENT = 'pending_shipment',
  SHIPPED = 'shipped',
  CANCELLED_BY_USER = 'canceled_by_user',
  CANCELLED_BY_SELLER = 'canceled_by_seller',
  DECLINED = 'declined',
  PENDING_DELIVERY = 'pending_delivery',
  DELIVERED = 'delivered',
  COULD_NOT_BE_DELIVERED = 'could_not_be_delivered',
  DELIVERED_VALID_FOR_RETURN = 'delivered_valid_for_return',
  COMPLETED = 'completed',
  RETURN_REQUESTED = 'return_requested',
  RETURN_ACCEPTED = 'return_accepted',
  RETURN_DECLINED = 'return_declined',
  RETURNED = 'returned',
  PENDING_PAYMENT = 'pending_payment',
  READY_FOR_PICKUP = 'ready_for_pickup',
  ACCEPTANCE_EXPIRED = 'acceptance_expired'
}

export interface OrderStatusHistoryEntry {
  date: number
  description: string
  // TODO: fix type
  extras: any
  status: OrderStatus
  title: string
  isPending: boolean
}

export interface MarketplaceItem {
  id: number
  orderId: number
  pricePerItem: number
  product: CartSellerItemProduct
  quantity: number
  hasBeenPartiallyRefunded: boolean
  remainingAmountForRefund: number
  remainingQuantityForRefund: number
}

export interface OrderTracking {
  company: string
  trackingNumber: number | string
  trackingUrl: string
}

export interface OrderInvoicingInfo {
  vat: string
  doy: string
  company: string
  occupation: string
  streetName: string
  streetNumber: string
  postcode: string
  city: string
}

export interface OrderPayment {
  created: number
  platformFee: number
  paymentMethod: MarketplacePayingMethod
  id: number
  orderId: number
  amount: number
  type: string
  order: Order
}

export interface Order {
  buyer: Buyer
  tracking?: OrderTracking
  checkoutSession: OrderCheckoutSession
  created: number
  fullAddress: string
  id: number
  image: string
  thumbs: string[]
  payment: OrderPayment
  items: MarketplaceItem[]
  nextCartSeller: CartSellerInfo | null
  seller: CartSellerInfo
  sellerReview?: MarketplaceReview
  status: OrderStatus
  shippingAddress?: ShippingAddress
  statusHistory: OrderStatusHistoryEntry[]
  timeline: OrderStatusHistoryEntry[]
  shippingPrice: number
  itemsPrice: number
  shippingCostAvailableForRefund?: number
  shippingInfo: CartShippingTypeCostOfShipping
  invoicingInfo?: OrderInvoicingInfo
  refundStatus?: OrderRefundStatus
  receiptUrl?: string
}

export interface OrdersResult {
  orders: {
    pagination: Pagination
    rows: Order[]
  }
}

export interface OrderItemToRefund {
  enabled: boolean
  amount: number
  productId: number
  quantity: number
}

export enum OrderRefundType {
  PRODUCTS = 'products',
  SHIPPING = 'shipping',
  ALL = 'all'
}

export interface MarketplacePayingMethod {
  type: string
  cardLast4Digits: string
  cardBrand: string
  isSaved?: boolean
}

export interface OrderRefundItemHistory {
  created: number
  orderId: number
  items: OrderItemToRefund[]
  paymentMethod: MarketplacePayingMethod
  refundReason: string
  refundType: OrderRefundType
  order: Order | null
  amount: number
  type: string
  id: number
}

export interface TimelineOrderRefundItemHistory {
  amount: number
  quantity: number
  items: MarketplaceItem[]
}

export enum OrderRefundStatus {
  PARTIAL_REFUND = 'partial_refund',
  FULL_REFUND = 'full_refund'
}
export interface OrderRefundInfo {
  items: MarketplaceItem[]
  refunds: OrderRefundItemHistory[]
  refundStatus: OrderRefundStatus
  schema: SchemaField[]
  shippingCost: number
  shippingCostAvailableForRefund: number
}

export interface OrderResult {
  order: Order
}

export interface CourierSchemaResult {
  schema: SchemaField[]
}

export interface MarketplaceNotesSchemaValues {
  sellerNotes: string
}
export interface MarketplaceNotesSchemaResult {
  schema?: SchemaField[]
  values: MarketplaceNotesSchemaValues
}

export interface CheckoutError {
  itemId: number
  error: string
}
